import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = ["Strategic Partner", "Investor", "Institution", "Accelerator"];

const PARTNERS = [
  "SFWRUNWAY",
  "ATELIER N°5",
  "MAISON LAB",
  "TEXTILE.IO",
  "NORTH FACTOR",
  "STUDIO PARALLEL",
  "ARCHIVE 22",
  "SIGNAL & STITCH",
];

export default function Partners() {
  const [form, setForm] = useState({
    name: "",
    organization: "",
    email: "",
    interest: "Strategic Partner",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.organization || !form.email) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/partner-inquiries`, form);
      setDone(true);
      toast.success("Inquiry received.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send. Try again.";
      toast.error(typeof msg === "string" ? msg : "Failed to send.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="partners"
      data-testid="partners-section"
      className="relative bg-black text-white border-t border-white/5"
    >
      {/* Marquee of partner names */}
      <div className="overflow-hidden border-b border-white/10 py-8">
        <div className="flex gap-16 whitespace-nowrap animate-marquee">
          {[...PARTNERS, ...PARTNERS, ...PARTNERS].map((p, i) => (
            <span
              key={i}
              className="font-serif text-2xl md:text-3xl text-white/30 tracking-wide flex items-center gap-16"
            >
              {p}
              <span className="text-white/15">✦</span>
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-y-12 md:gap-x-16">
          <div className="col-span-12 md:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
              §06 — Partners & Investors
            </p>
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            >
              Collaborate with the <span className="italic font-light">Foundry</span>.
            </motion.h2>
            <p className="mt-8 text-white/65 text-base md:text-lg font-light leading-relaxed max-w-md">
              Runway Foundry collaborates with aligned partners interested in
              shaping a more sustainable and technologically advanced future for
              fashion.
            </p>

            <div className="mt-10 space-y-4 text-xs uppercase tracking-[0.25em] text-white/45">
              <div>· Strategic Partners</div>
              <div>· Institutional Investors</div>
              <div>· Cultural Institutions</div>
              <div>· Accelerators & Co-builders</div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7">
            {done ? (
              <div
                data-testid="partner-success"
                className="border border-white/20 p-10 md:p-14"
              >
                <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6">Noted</p>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight">
                  Thank you. A partner will be in <span className="italic font-light">touch</span>.
                </h3>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-10" data-testid="partner-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <PField
                    label="Name"
                    required
                    testid="partner-name"
                    value={form.name}
                    onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                    placeholder="Your name"
                  />
                  <PField
                    label="Organization"
                    required
                    testid="partner-org"
                    value={form.organization}
                    onChange={(v) => setForm((f) => ({ ...f, organization: v }))}
                    placeholder="Firm or institution"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <PField
                    label="Email"
                    required
                    type="email"
                    testid="partner-email"
                    value={form.email}
                    onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                    placeholder="you@firm.com"
                  />
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">Interest</span>
                    <select
                      data-testid="partner-interest"
                      value={form.interest}
                      onChange={(e) => setForm((f) => ({ ...f, interest: e.target.value }))}
                      className="mt-3 w-full bg-transparent border-b border-white/15 py-3 text-white focus:border-white focus:outline-none text-base font-light appearance-none cursor-pointer"
                    >
                      {INTERESTS.map((o) => (
                        <option key={o} value={o} className="bg-black text-white">{o}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <PField
                  label="Message"
                  multiline
                  testid="partner-message"
                  value={form.message}
                  onChange={(v) => setForm((f) => ({ ...f, message: v }))}
                  placeholder="Tell us about your interest, focus area, and timeline."
                />

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="partner-submit-button"
                    className="group inline-flex items-center justify-center gap-3 border border-white/40 hover:border-white text-white px-10 py-4 text-xs uppercase tracking-[0.25em] font-medium transition-all disabled:opacity-50 hover:bg-white/5"
                  >
                    {loading ? "Sending…" : "Partner With Us"}
                    <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function PField({ label, value, onChange, placeholder, type = "text", required, multiline, testid }) {
  const cls =
    "w-full bg-transparent border-b border-white/15 py-3 text-white placeholder:text-white/25 focus:border-white focus:outline-none transition-colors text-base font-light";
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">
        {label}
        {required && <span className="text-white/70"> *</span>}
      </span>
      {multiline ? (
        <textarea
          data-testid={testid}
          required={required}
          rows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`${cls} resize-none mt-3`}
        />
      ) : (
        <input
          data-testid={testid}
          required={required}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`${cls} mt-3`}
        />
      )}
    </label>
  );
}
